import type { ComponentDefinition, Terminal } from "../types";

type AcPole = "l1" | "l2" | "n" | "pe";

function watchdogPort(prefix: "in" | "out"): Terminal[] {
  const poles: AcPole[] = ["l1", "l2", "n", "pe"];
  return poles.map((pole, index) => ({
    id: `ac-${prefix}-${pole}`,
    label: `${prefix === "in" ? "SOURCE" : "RV"} ${pole === "pe" ? "GROUND" : pole.toUpperCase()}`,
    polarity: pole === "pe" ? "ground" : "neutral",
    direction: prefix === "in" ? "input" : "output",
    maxAmp: 50,
    side: prefix === "in" ? "left" : "right",
    offset: 0.15 + index * 0.23,
  }));
}

function penguin(model: "651815C75X" | "651816C75X", capacity: string): ComponentDefinition {
  return {
    id: `load-dometic-penguin-ii-${model.toLowerCase()}`,
    name: `Dometic Penguin II ${capacity} RV Air Conditioner / Heat Pump`,
    shortName: `Penguin II ${capacity}`,
    subtitle: `${model} · 120 V AC · 20 A circuit`,
    category: "load",
    manufacturer: "Dometic",
    model,
    acVoltage: 120,
    // Dometic installation instructions 3313461.018, printed p. 4:
    // 651815C75X: compressor 12.7 A + fan 2.6 A; 651816C75X: 13.0 + 2.6 A.
    // Both require 20 A circuit protection. This is not a 20 A operating load.
    // Do not turn RMS current into real watts without a published power factor,
    // or feed AC current into the DC ampDraw/ampSurge demand model.
    // https://media.dometic.com/externalassets/dometic-penguin-ii-13-5k-with-heat-pump_73164.pdf
    width: 210,
    height: 140,
    // Published branch protection does not establish connector ampacity.
    // These are the mains connections; thermostat/control wiring is omitted.
    terminals: [
      { id: "ac-l", label: "SUPPLY L", polarity: "neutral", direction: "input", maxAmp: 0, side: "left", offset: 0.2 },
      { id: "ac-n", label: "SUPPLY N", polarity: "neutral", direction: "input", maxAmp: 0, side: "left", offset: 0.5 },
      { id: "ac-pe", label: "GROUND", polarity: "ground", direction: "input", maxAmp: 0, side: "left", offset: 0.8 },
    ],
  };
}

export const RV_AC: ComponentDefinition[] = [
  {
    // Original Bluetooth PWD50-EPO-H, not the Gen 2 Wi-Fi EPOHW model.
    // Manufacturer installation guide identifies separate L1/L2/N/G input
    // and output conductors, 50 A service and 120 V line-to-neutral.
    // https://hughesautoformers.com/wp-content/uploads/2019/02/PWD-EPO-H-instructions.pdf
    // https://hughesautoformers.com/product/pwd50-epo-h/
    id: "dist-hughes-pwd50-epo-h",
    name: "Hughes Power Watchdog PWD50-EPO-H 50A Surge Protector",
    shortName: "Power Watchdog 50A",
    subtitle: "Hardwired RV surge protector with EPO",
    category: "distribution",
    manufacturer: "Hughes Autoformers",
    model: "PWD50-EPO-H",
    acVoltage: 120,
    width: 220,
    height: 160,
    terminals: [...watchdogPort("in"), ...watchdogPort("out")],
    // A normal connected wiring view only. No invented fault/trip behavior,
    // neutral switching, surge suppression, delay or branch-breaker rating.
    // Explicit pole links prevent the generic distribution bus from joining
    // the two hot legs, neutral and earth together.
    simulateStates: {
      default: "connected",
      options: [{
        id: "connected",
        label: "Connected",
        intraLinks: (["l1", "l2", "n", "pe"] as const).map(pole =>
          [`ac-in-${pole}`, `ac-out-${pole}`] as const),
      }],
    },
  },
  penguin("651815C75X", "13.5K"),
  penguin("651816C75X", "High Capacity"),
];
